import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import starWebPRNTService from "../../services/starWebPRNTService";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  IconButton,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Card,
  CardContent,
  CardMedia,
  Badge,
  Tooltip,
  Drawer,
  Fab,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  ShoppingCart as CartIcon,
} from "@mui/icons-material";
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Delete as DeleteIcon,
  Payment as PaymentIcon,
  PhoneAndroid as MobileIcon,
} from "@mui/icons-material";
import api from "../../services/api";
import { toast } from "react-hot-toast";
import HeldTransactions from "./HeldTransactions";

const POSMain = () => {
  const { storeId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(0);
  const [currentOrder, setCurrentOrder] = useState({
    items: [],
    subtotal: 0,
    standardTax: 0,    // 標準税率10%分
    reducedTax: 0,     // 軽減税率8%分
    tax: 0,
    total: 0,
  });
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [receivedAmount, setReceivedAmount] = useState("");
  const [cartDrawerOpen, setCartDrawerOpen] = useState(false);
  // 税率定数（商品ごとのtaxRateを使用、デフォルトは10%）
  const STANDARD_TAX_RATE = 0.10;  // 標準税率10%
  const REDUCED_TAX_RATE = 0.08;   // 軽減税率8%

  // モバイルオーダー関連のstate追加
  const [mobileOrders, setMobileOrders] = useState([]);
  const [selectedMobileOrder, setSelectedMobileOrder] = useState(null);
  const [selectedTableGroup, setSelectedTableGroup] = useState(null);
  const [mobileOrderDialogOpen, setMobileOrderDialogOpen] = useState(false);
  const [orderView, setOrderView] = useState("POS"); // 'POS' or 'MOBILE'

  useEffect(() => {
    fetchCategories();
    fetchProducts();
    fetchMobileOrders(); // モバイルオーダーの取得を追加
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId]);

  const fetchCategories = async () => {
    try {
      const response = await api.get(`/api/menu/${storeId}/categories`);
      setCategories(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("カテゴリーの取得に失敗しました:", error);
      setCategories([]);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await api.get(`/api/menu/${storeId}/products`);
      const productList = Array.isArray(response.data) ? response.data : [];
      setProducts(productList);
    } catch (error) {
      console.error("商品の取得に失敗しました:", error);
      setProducts([]);
    }
  };

  const fetchMobileOrders = async () => {
    try {
      const response = await api.get(`/api/orders`, {
        params: {
          storeId,
          orderType: "MOBILE",
          paymentStatus: "UNPAID",
        },
      });
      setMobileOrders(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("モバイルオーダーの取得に失敗しました:", error);
      setMobileOrders([]);
    }
  };

  // テーブル単位でモバイルオーダーをグループ化
  const groupedMobileOrders = useMemo(() => {
    const groups = {};

    mobileOrders.forEach(order => {
      const sessionId = order.session_id;
      const tableSession = order.table_sessions;
      const paymentDetails = order.payment_details || order.paymentDetails || {};
      const tableNumber = tableSession?.table_number || paymentDetails.tableNumber || order.tableNumber;
      const area = tableSession?.area || paymentDetails.area || order.area || "";
      const created = order.createdAt || order.created_at;
      const orderDate = created ? new Date(created).toDateString() : '';

      // グループキー
      let groupKey;
      if (sessionId) {
        groupKey = sessionId;
      } else if (tableNumber) {
        groupKey = `table-${tableNumber}-${area}-${orderDate}`;
      } else {
        groupKey = `order-${order.id || order._id}`;
      }

      if (!groups[groupKey]) {
        groups[groupKey] = {
          sessionId,
          tableNumber: tableNumber || "-",
          area,
          orders: [],
          totalAmount: 0,
          tipTotal: 0,
        };
      }

      const orderTotal = order.total ?? order.total_amount ?? order.totalAmount ?? 0;
      const orderTip = order.tipTotal || order.tip_total || 0;

      groups[groupKey].orders.push(order);
      groups[groupKey].totalAmount += orderTotal;
      groups[groupKey].tipTotal += orderTip;
    });

    return Object.entries(groups)
      .map(([key, group]) => ({ key, ...group }))
      .sort((a, b) => b.orders.length - a.orders.length);
  }, [mobileOrders]);

  const handleAddItem = (product) => {
    const productId = product._id || product.id;

    // BUG-004修正: 在庫管理が有効で在庫が0以下の場合は追加を拒否
    if (product.stockManagement?.enabled) {
      const currentStock = product.stockManagement.currentStock || 0;
      const existingItem = currentOrder.items.find(
        (item) => (item.product._id || item.product.id) === productId
      );
      const cartQuantity = existingItem ? existingItem.quantity : 0;

      if (currentStock <= cartQuantity) {
        toast.error(`「${getTranslatedText(product.name)}」は在庫切れです`);
        return;
      }
    }

    const existingItem = currentOrder.items.find(
      (item) => (item.product._id || item.product.id) === productId
    );

    if (existingItem) {
      const updatedItems = currentOrder.items.map((item) =>
        (item.product._id || item.product.id) === productId
          ? { ...item, quantity: item.quantity + 1 }
          : item
      );
      updateOrder(updatedItems);
    } else {
      const newItem = {
        product,
        quantity: 1,
        price: product.price,
      };
      updateOrder([...currentOrder.items, newItem]);
    }
  };

  const handleUpdateQuantity = (itemIndex, change) => {
    const updatedItems = currentOrder.items.map((item, index) => {
      if (index === itemIndex) {
        const newQuantity = item.quantity + change;
        return newQuantity > 0 ? { ...item, quantity: newQuantity } : null;
      }
      return item;
    });

    updateOrder(updatedItems.filter(Boolean));
  };

  const handleRemoveItem = (itemIndex) => {
    const updatedItems = currentOrder.items.filter(
      (_, index) => index !== itemIndex
    );
    updateOrder(updatedItems);
  };

  const updateOrder = (items) => {
    // TAX-1 FIX: 税込価格として計算（バックエンドと統一）
    // 日本の小売標準: 表示価格は税込（内税）
    let standardTotal = 0;  // 標準税率10%対象（税込）
    let reducedTotal = 0;   // 軽減税率8%対象（税込）

    items.forEach(item => {
      const itemTotal = item.price * item.quantity;
      // 商品のtaxRateを取得（デフォルトは10）
      const taxRate = item.product?.taxRate || item.product?.tax_rate || 10;

      if (taxRate === 8) {
        reducedTotal += itemTotal;
      } else {
        standardTotal += itemTotal;
      }
    });

    // 税込価格から内税を計算（切り捨て）
    const standardTax = Math.floor(standardTotal - standardTotal / (1 + STANDARD_TAX_RATE));
    const reducedTax = Math.floor(reducedTotal - reducedTotal / (1 + REDUCED_TAX_RATE));
    const tax = standardTax + reducedTax;

    // 合計 = 税込価格の合計（税を加算しない）
    const total = standardTotal + reducedTotal;
    // 小計（税抜） = 合計 - 税
    const subtotal = total - tax;

    setCurrentOrder({
      items,
      subtotal,
      standardTax,
      reducedTax,
      tax,
      total,
    });
  };

  const handlePayment = async () => {
    try {
      // 1. 注文を作成
      const orderData = {
        storeId: storeId,
        items: currentOrder.items.map((item) => ({
          productId: item.product._id || item.product.id,
          product_id: item.product._id || item.product.id,
          name: typeof item.product.name === 'object' ? item.product.name.ja : item.product.name,
          quantity: item.quantity,
          price: item.price,
          unit_price: item.price,
        })),
        subtotal: currentOrder.subtotal,
        sub_total: currentOrder.subtotal,
        tax: currentOrder.tax,
        tax_amount: currentOrder.tax,
        total: currentOrder.total,
        total_amount: currentOrder.total,
        totalAmount: currentOrder.total,
        paymentMethod: "cash",
        orderType: "POS",
        status: "PENDING",
      };

      const orderRes = await api.post(`/api/orders`, orderData);
      const orderId = orderRes.data.id || orderRes.data._id || orderRes.data.orderId;

      // 1.5. キッチン印刷（非同期・失敗しても決済は続行）
      // 自動印刷トグル + カテゴリ別プリンタ振り分け対応
      if (starWebPRNTService.isAutoKitchenPrintEnabled()) {
        starWebPRNTService.printKitchenTicketWithRouting({
          tableNumber: currentOrder.tableNumber,
          items: currentOrder.items.map((item) => ({
            name: typeof item.product.name === 'object' ? item.product.name.ja : item.product.name,
            quantity: item.quantity,
            note: item.note,
            categoryId: item.product.categoryId || item.product.category_id,
          })),
        }).catch((err) => console.warn('[Kitchen] 印刷スキップ:', err.message));
      }

      // 2. 支払いを処理
      const paymentData = {
        orderId: orderId,
        totalAmount: currentOrder.total,
        paymentMethod: "cash",
        // M-2 FIX: 浮動小数点対策
        receivedAmount: Math.round(parseFloat(receivedAmount)),
      };

      await api.post(`/api/sales/cash`, paymentData);

      toast.success("支払いが完了しました");

      // M-1 FIX: 注文完了後のstate完全リセット
      setCurrentOrder({
        items: [],
        subtotal: 0,
        standardTax: 0,
        reducedTax: 0,
        tax: 0,
        total: 0,
      });
      setPaymentDialog(false);
      setReceivedAmount("");
    } catch (error) {
      console.error("注文の処理に失敗しました:", error);
      toast.error("支払い処理に失敗しました");
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("ja-JP", {
      style: "currency",
      currency: "JPY",
    }).format(value);
  };

  const getTranslatedText = (translationObj, defaultLang = 'ja') => {
    if (!translationObj) return '';
    
    if (typeof translationObj !== 'object') return translationObj;
    
    return translationObj[defaultLang] || translationObj.ja || translationObj.en || translationObj.zh || '';
  };

  const ProductCard = ({ product }) => {
    const imageUrl = product.imageUrl || product.image_url || product.thumbnail_url || product.thumbnailUrl;
    const isOutOfStock = product.stockManagement?.enabled && (product.stockManagement.currentStock || 0) <= 0;

    return (
      <Card
        sx={{
          cursor: isOutOfStock ? "not-allowed" : "pointer",
          height: "100%",
          opacity: isOutOfStock ? 0.5 : 1,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'stretch',
          transition: 'all 0.2s ease',
          '&:hover': {
            transform: isOutOfStock ? 'none' : 'translateY(-2px)',
            boxShadow: isOutOfStock ? 1 : 4,
          }
        }}
        onClick={() => !isOutOfStock && handleAddItem(product)}
        data-testid={`product-${product._id || product.id}`}
      >
        {/* 商品サムネイル（常に表示） */}
        <Box
          sx={{
            width: 70,
            minWidth: 70,
            height: 70,
            bgcolor: imageUrl ? 'transparent' : 'grey.200',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            flexShrink: 0,
          }}
        >
          {imageUrl ? (
            <CardMedia
              component="img"
              image={imageUrl}
              alt={getTranslatedText(product.name)}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          ) : (
            <Typography variant="h5" color="grey.400">
              🍽️
            </Typography>
          )}
        </Box>

        <CardContent sx={{
          p: 1,
          pl: 1.5,
          "&:last-child": { pb: 1 },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          flex: 1,
          minWidth: 0,
        }}>
          <Typography
            variant="body2"
            component="div"
            fontWeight="medium"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {getTranslatedText(product.name)}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
            <Typography variant="body1" color="primary" fontWeight="bold">
              ¥{(product.price || 0).toLocaleString()}
            </Typography>
            {isOutOfStock && (
              <Chip label="売切" size="small" color="error" sx={{ height: 20, fontSize: '0.7rem' }} />
            )}
            {product.stockManagement?.enabled && !isOutOfStock && (
              <Typography variant="caption" color="text.secondary">
                残{product.stockManagement.currentStock}
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>
    );
  };

  // モバイルオーダーの支払い処理 - Checkout画面に遷移

  const handleMobileOrderCheckout = (mobileOrder) => {
    if (!mobileOrder) {
      toast.error("注文情報の取得に失敗しました");
      return;
    }

    const orderId = mobileOrder._id || mobileOrder.id;
    const items = (mobileOrder.items || mobileOrder.order_items || []).map(item => ({
      productId: item.productId || item.product_id,
      name: item.name || item.product_name,
      price: item.price || item.unit_price,
      quantity: item.quantity,
    }));

    const total = mobileOrder.totalAmount || mobileOrder.total_amount || mobileOrder.total || 0;
    const subtotal = mobileOrder.subtotal || mobileOrder.sub_total || Math.round(total / 1.1);
    const tax = mobileOrder.tax || mobileOrder.tax_amount || total - subtotal;

    // チップ情報を取得（APIレスポンスはcamelCaseに変換される）
    const tipTotal = mobileOrder.tipTotal || mobileOrder.tip_total || 0;

    // Checkout画面に渡すオーダーオブジェクト
    const orderForCheckout = {
      id: orderId,
      existingOrderId: orderId, // 既存注文であることを示すフラグ
      items: items,
      total: total,
      subtotal: subtotal,
      tax: tax,
      tipTotal: tipTotal,
      tableNumber: mobileOrder.table_sessions?.table_number || mobileOrder.tableNumber || "",
      area: mobileOrder.table_sessions?.area || "",
      orderType: "MOBILE",
    };

    // ダイアログを閉じてCheckout画面に遷移
    setMobileOrderDialogOpen(false);
    setSelectedMobileOrder(null);
    navigate(`/pos/${storeId}/checkout`, { state: { order: orderForCheckout } });
  };

  // テーブル単位の支払い処理 - Checkout画面に遷移
  const handleTableGroupCheckout = (tableGroup) => {
    if (!tableGroup || !tableGroup.orders || tableGroup.orders.length === 0) {
      toast.error("注文情報の取得に失敗しました");
      return;
    }

    // 全注文の商品を集約
    const itemMap = new Map();
    tableGroup.orders.forEach(order => {
      (order.items || order.order_items || []).forEach(item => {
        const itemName = typeof item.name === 'object' ? (item.name.ja || item.name.en || '') : (item.name || item.product_name || '');
        const key = `${item.productId || item.product_id}-${item.price}`;
        if (itemMap.has(key)) {
          itemMap.get(key).quantity += item.quantity;
        } else {
          itemMap.set(key, {
            productId: item.productId || item.product_id,
            name: itemName,
            price: item.price || item.unit_price,
            quantity: item.quantity,
          });
        }
      });
    });
    const items = Array.from(itemMap.values());

    const total = tableGroup.totalAmount;
    const subtotal = Math.round(total / 1.1);
    const tax = total - subtotal;
    const tipTotal = tableGroup.tipTotal;

    // 注文IDリスト（全注文のIDを保持）
    const orderIds = tableGroup.orders.map(o => o._id || o.id);

    // Checkout画面に渡すオーダーオブジェクト
    const orderForCheckout = {
      id: orderIds[0], // 最初の注文IDを代表として使用
      existingOrderId: orderIds[0],
      existingOrderIds: orderIds, // 全注文IDリスト
      items: items,
      total: total,
      subtotal: subtotal,
      tax: tax,
      tipTotal: tipTotal,
      tableNumber: tableGroup.tableNumber || "",
      area: tableGroup.area || "",
      orderType: "MOBILE",
      isTableGroup: true, // テーブル単位の支払いであることを示すフラグ
    };

    // ダイアログを閉じてCheckout画面に遷移
    setMobileOrderDialogOpen(false);
    setSelectedTableGroup(null);
    navigate(`/pos/${storeId}/checkout`, { state: { order: orderForCheckout } });
  };


  // モバイルオーダー一覧表示（テーブル単位）
  const MobileOrderList = () => (
    <List>
      {groupedMobileOrders.length === 0 ? (
        <ListItem>
          <ListItemText
            primary="未払いの注文はありません"
            sx={{ textAlign: 'center', color: 'text.secondary' }}
          />
        </ListItem>
      ) : (
        groupedMobileOrders.map((group) => (
          <ListItem key={group.key} disablePadding>
            <ListItemButton
              onClick={() => {
                setSelectedTableGroup(group);
                setMobileOrderDialogOpen(true);
              }}
              selected={selectedTableGroup?.key === group.key}
            >
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {group.tableNumber !== "-" ? `テーブル ${group.tableNumber}` : "レジ注文"}
                    </Typography>
                    {group.area && (
                      <Chip label={group.area} size="small" variant="outlined" />
                    )}
                  </Box>
                }
                secondary={
                  <>
                    <Typography variant="body2">
                      {group.orders.length}件の注文
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      合計: ¥{group.totalAmount.toLocaleString()}
                      {group.tipTotal > 0 && ` (+投げ銭 ¥${group.tipTotal.toLocaleString()})`}
                    </Typography>
                  </>
                }
              />
              <Chip label="未払い" color="warning" size="small" sx={{ ml: 1 }} />
            </ListItemButton>
          </ListItem>
        ))
      )}
    </List>
  );

  // モバイルオーダー支払いダイアログ（テーブル単位）
  const MobileOrderDialog = () => {
    // テーブル内の全商品を集約
    const getAllItems = () => {
      if (!selectedTableGroup) return [];
      const itemMap = new Map();
      selectedTableGroup.orders.forEach(order => {
        (order.items || []).forEach(item => {
          const itemName = getTranslatedText(item.name);
          const key = `${itemName}-${item.price}`;
          if (itemMap.has(key)) {
            itemMap.get(key).quantity += item.quantity;
          } else {
            itemMap.set(key, { name: itemName, price: item.price, quantity: item.quantity });
          }
        });
      });
      return Array.from(itemMap.values());
    };

    return (
      <Dialog
        open={mobileOrderDialogOpen}
        onClose={() => { setMobileOrderDialogOpen(false); setSelectedTableGroup(null); }}
        maxWidth="sm"
        fullWidth
      >
        {selectedTableGroup && (
          <>
            <DialogTitle>
              {selectedTableGroup.tableNumber !== "-"
                ? `テーブル ${selectedTableGroup.tableNumber}`
                : "レジ注文"}
              {selectedTableGroup.area && ` (${selectedTableGroup.area})`}
            </DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {selectedTableGroup.orders.length}件の注文
              </Typography>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                合計: ¥{selectedTableGroup.totalAmount.toLocaleString()}
              </Typography>
              {selectedTableGroup.tipTotal > 0 && (
                <Typography variant="body1" color="primary" gutterBottom>
                  投げ銭: ¥{selectedTableGroup.tipTotal.toLocaleString()}
                </Typography>
              )}
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>注文商品</Typography>
              <List dense>
                {getAllItems().map((item, index) => (
                  <ListItem key={index}>
                    <ListItemText
                      primary={item.name}
                      secondary={`${item.quantity}個 × ¥${item.price.toLocaleString()}`}
                    />
                    <Typography>¥{(item.price * item.quantity).toLocaleString()}</Typography>
                  </ListItem>
                ))}
              </List>
            </DialogContent>
            <DialogActions sx={{ flexDirection: 'column', gap: 1, p: 2 }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                fullWidth
                onClick={() => handleTableGroupCheckout(selectedTableGroup)}
                sx={{ py: 2 }}
              >
                お会計へ進む（¥{(selectedTableGroup.totalAmount + selectedTableGroup.tipTotal).toLocaleString()}）
              </Button>
              <Button
                variant="text"
                fullWidth
                onClick={() => { setMobileOrderDialogOpen(false); setSelectedTableGroup(null); }}
              >
                キャンセル
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    );
  };

  // カート内容コンポーネント（モバイル用Drawerとデスクトップ用サイドバー共通）
  const CartContent = () => (
    <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h6" gutterBottom>
        注文内容
      </Typography>

      <Paper sx={{ mb: 2, flex: 1, overflow: "auto" }}>
        <List>
          {currentOrder.items.map((item, index) => (
            <ListItem key={index} sx={{ pr: 12 }}>
              <ListItemText
                primary={getTranslatedText(item.product.name)}
                secondary={formatCurrency(item.price)}
              />
              <ListItemSecondaryAction>
                <IconButton size="small" onClick={() => handleUpdateQuantity(index, -1)}>
                  <RemoveIcon />
                </IconButton>
                <Typography component="span" sx={{ mx: 1 }}>{item.quantity}</Typography>
                <IconButton size="small" onClick={() => handleUpdateQuantity(index, 1)}>
                  <AddIcon />
                </IconButton>
                <IconButton size="small" onClick={() => handleRemoveItem(index)}>
                  <DeleteIcon />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      </Paper>

      <Box sx={{ mb: 2 }}>
        <Grid container spacing={1}>
          <Grid item xs={6}><Typography>小計</Typography></Grid>
          <Grid item xs={6} sx={{ textAlign: "right" }}>
            <Typography>{formatCurrency(currentOrder.subtotal)}</Typography>
          </Grid>
          <Grid item xs={6}><Typography>消費税</Typography></Grid>
          <Grid item xs={6} sx={{ textAlign: "right" }}>
            <Typography>{formatCurrency(currentOrder.tax)}</Typography>
          </Grid>
          <Grid item xs={6}><Typography variant="h6">合計</Typography></Grid>
          <Grid item xs={6} sx={{ textAlign: "right" }}>
            <Typography variant="h6">{formatCurrency(currentOrder.total)}</Typography>
          </Grid>
        </Grid>
      </Box>

      <Button
        variant="contained"
        fullWidth
        size="large"
        startIcon={<PaymentIcon />}
        onClick={() => {
          setPaymentDialog(true);
          if (isMobile) setCartDrawerOpen(false);
        }}
        disabled={currentOrder.items.length === 0}
      >
        会計する
      </Button>
    </Box>
  );

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: isMobile ? 'column' : 'row' }}>
      {/* 商品選択エリア */}
      <Box sx={{
        flex: isMobile ? 1 : 2,
        p: isMobile ? 1 : 2,
        borderRight: isMobile ? 0 : 1,
        borderColor: "divider",
        overflow: 'auto',
        pb: isMobile ? 10 : 2,
      }}>
        {/* ヘッダー: モバイルオーダー切替アイコン */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {orderView === "POS" ? "商品選択" : "モバイルオーダー"}
          </Typography>
          <Tooltip title={orderView === "POS" ? "モバイルオーダーを表示" : "POSレジに戻る"}>
            <IconButton
              onClick={() => setOrderView(orderView === "POS" ? "MOBILE" : "POS")}
              color={orderView === "MOBILE" ? "secondary" : "default"}
              sx={{
                bgcolor: orderView === "MOBILE" ? 'secondary.light' : 'transparent',
                '&:hover': { bgcolor: orderView === "MOBILE" ? 'secondary.main' : 'action.hover' }
              }}
            >
              <Badge
                badgeContent={groupedMobileOrders.length}
                color="error"
                invisible={groupedMobileOrders.length === 0}
              >
                <MobileIcon />
              </Badge>
            </IconButton>
          </Tooltip>
        </Box>

        {orderView === "POS" ? (
          <>
            <Tabs
              value={selectedCategory}
              onChange={(e, newValue) => setSelectedCategory(newValue)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ mb: 2 }}
            >
              <Tab label="すべて" />
              {Array.isArray(categories) &&
                categories.map((category) => (
                  <Tab key={category._id || category.id} label={getTranslatedText(category.name)} />
                ))}
            </Tabs>

            <Grid container spacing={2}>
              {Array.isArray(products) &&
                products
                  .filter((product) => {
                    if (selectedCategory === 0) return true;
                    const selectedCat = categories[selectedCategory - 1];
                    if (!selectedCat) return false;
                    
                    // カテゴリIDを取得（複数の形式に対応）
                    const selectedCatId = String(selectedCat._id || selectedCat.id);
                    
                    // 商品のカテゴリIDを取得（複数の形式に対応）
                    const productCatId = String(
                      product.category?._id || 
                      product.category?.id || 
                      product.category_id || 
                      product.categoryId ||
                      product.category
                    );
                    
                    return productCatId === selectedCatId;
                  })
                  .map((product) => (
                    <Grid item xs={6} sm={4} md={3} key={product._id || product.id}>
                      <ProductCard product={product} />
                    </Grid>
                  ))}
            </Grid>
          </>
        ) : (
          <Box>
            <Typography variant="h6" gutterBottom>
              未払いのモバイルオーダー
            </Typography>
            <MobileOrderList />
          </Box>
        )}
      </Box>

      {/* 右側: 注文内容/支払いエリア（デスクトップのみ表示） */}
      {!isMobile && (
        <Box sx={{ flex: 1, p: 2, bgcolor: "grey.100" }}>
          {orderView === "POS" ? (
            <>
              <Typography variant="h6" gutterBottom>
                注文内容
              </Typography>

              <Paper sx={{ mb: 2, maxHeight: "calc(100vh - 300px)", overflow: "auto" }}>
                <List>
                  {currentOrder.items.map((item, index) => (
                    <ListItem key={index}>
                      <ListItemText
                        primary={getTranslatedText(item.product.name)}
                        secondary={formatCurrency(item.price)}
                      />
                      <ListItemSecondaryAction>
                        <IconButton size="small" onClick={() => handleUpdateQuantity(index, -1)}>
                          <RemoveIcon />
                        </IconButton>
                        <Typography component="span" sx={{ mx: 1 }}>{item.quantity}</Typography>
                        <IconButton size="small" onClick={() => handleUpdateQuantity(index, 1)}>
                          <AddIcon />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleRemoveItem(index)}>
                          <DeleteIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </Paper>

              <Box sx={{ mb: 2 }}>
                <Grid container spacing={1}>
                  <Grid item xs={6}><Typography>小計</Typography></Grid>
                  <Grid item xs={6} sx={{ textAlign: "right" }}>
                    <Typography>{formatCurrency(currentOrder.subtotal)}</Typography>
                  </Grid>
                  <Grid item xs={6}><Typography>消費税</Typography></Grid>
                  <Grid item xs={6} sx={{ textAlign: "right" }}>
                    <Typography>{formatCurrency(currentOrder.tax)}</Typography>
                  </Grid>
                  <Grid item xs={6}><Typography variant="h6">合計</Typography></Grid>
                  <Grid item xs={6} sx={{ textAlign: "right" }}>
                    <Typography variant="h6">{formatCurrency(currentOrder.total)}</Typography>
                  </Grid>
                </Grid>
              </Box>

              <HeldTransactions
                compact
                currentItems={currentOrder.items.map((item) => ({
                  productId: item.product._id || item.product.id,
                  name: typeof item.product.name === 'object' ? item.product.name.ja : item.product.name,
                  price: item.price,
                  quantity: item.quantity,
                  taxRate: 10,
                }))}
                onHold={() => {
                  // M-1 FIX: state完全リセット
                  setCurrentOrder({ items: [], subtotal: 0, standardTax: 0, reducedTax: 0, tax: 0, total: 0 });
                  toast.success("取引を保留しました");
                }}
                onRecall={(transaction) => {
                  if (transaction?.items) {
                    const recalledItems = transaction.items.map((item) => ({
                      product: { _id: item.productId, id: item.productId, name: item.name, price: item.price },
                      quantity: item.quantity,
                      price: item.price,
                    }));
                    updateOrder(recalledItems);
                    toast.success("取引を呼び戻しました");
                  }
                }}
              />

              <Button
                variant="contained"
                fullWidth
                size="large"
                startIcon={<PaymentIcon />}
                onClick={() => navigate(`/pos/${storeId}/checkout`, { state: { order: currentOrder } })}
                disabled={currentOrder.items.length === 0}
                data-testid="confirm-order"
                sx={{
                  mt: 2,
                  py: 2,
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  bgcolor: currentOrder.items.length > 0 ? 'primary.main' : 'grey.300',
                  '&:not(:disabled)': {
                    animation: currentOrder.items.length > 0 ? 'pulse 2s infinite' : 'none',
                  },
                  '@keyframes pulse': {
                    '0%': { boxShadow: '0 0 0 0 rgba(25, 118, 210, 0.4)' },
                    '70%': { boxShadow: '0 0 0 10px rgba(25, 118, 210, 0)' },
                    '100%': { boxShadow: '0 0 0 0 rgba(25, 118, 210, 0)' },
                  },
                }}
              >
                {currentOrder.items.length > 0
                  ? `会計 ${formatCurrency(currentOrder.total)}`
                  : '会計'}
              </Button>
            </>
          ) : (
            <>
              <Typography variant="h6" gutterBottom>支払い処理</Typography>
              {selectedMobileOrder ? (
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    注文番号: {(selectedMobileOrder._id || selectedMobileOrder.id).slice(-6)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    テーブル: {selectedMobileOrder.tableNumber} ({selectedMobileOrder.area})
                  </Typography>
                  <List dense>
                    {selectedMobileOrder.items.map((item, index) => (
                      <ListItem key={index} sx={{ py: 0.5 }}>
                        <ListItemText
                          primary={getTranslatedText(item.name)}
                          secondary={`${item.quantity}個 × ¥${item.price.toLocaleString()}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                  <Typography variant="h5" sx={{ mt: 2, mb: 2, textAlign: 'right' }}>
                    ¥{selectedMobileOrder.totalAmount?.toLocaleString() || selectedMobileOrder.total_amount?.toLocaleString() || 0}
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    fullWidth
                    onClick={() => handleMobileOrderCheckout(selectedMobileOrder)}
                    sx={{ py: 2, mb: 1 }}
                  >
                    お会計へ進む
                  </Button>
                  <Button variant="text" fullWidth onClick={() => setSelectedMobileOrder(null)}>キャンセル</Button>
                </Paper>
              ) : (
                <Paper sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                  <MobileIcon sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
                  <Typography>左のリストから注文を選択してください</Typography>
                </Paper>
              )}
            </>
          )}
        </Box>
      )}

      {/* モバイル用: カートFABボタン */}
      {isMobile && orderView === "POS" && (
        <Fab
          color="primary"
          sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 1000 }}
          onClick={() => setCartDrawerOpen(true)}
        >
          <Badge badgeContent={currentOrder.items.length} color="error">
            <CartIcon />
          </Badge>
        </Fab>
      )}

      {/* モバイル用: カートDrawer */}
      <Drawer
        anchor="bottom"
        open={cartDrawerOpen}
        onClose={() => setCartDrawerOpen(false)}
        PaperProps={{ sx: { height: '80vh', borderTopLeftRadius: 16, borderTopRightRadius: 16 } }}
      >
        <CartContent />
      </Drawer>

      {/* 支払いダイアログ */}
      <Dialog open={paymentDialog} onClose={() => setPaymentDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>お支払い</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>合計: {formatCurrency(currentOrder.total)}</Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="受取金額" type="number" value={receivedAmount} onChange={(e) => setReceivedAmount(e.target.value)} />
            </Grid>
            {receivedAmount && (
              <Grid item xs={12}>
                <Typography>お釣り: {formatCurrency(Math.max(receivedAmount - currentOrder.total, 0))}</Typography>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentDialog(false)}>キャンセル</Button>
          <Button variant="contained" onClick={handlePayment} disabled={!receivedAmount || parseFloat(receivedAmount) < currentOrder.total}>確定</Button>
        </DialogActions>
      </Dialog>

      {/* モバイルオーダー支払いダイアログ */}
      <MobileOrderDialog />
    </Box>
  );
};

export default POSMain;
